import { useState, useRef, useEffect, useCallback, type ReactNode, ChangeEvent } from 'react';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import Spinner from './Spinner';

export interface AutocompleteProps<T> {
	placeholder?: string;
	onSearch: (query: string) => Promise<T[]>;
	onSelect: (item: T) => void;
	onClear?: () => void;
	renderItem: (item: T) => ReactNode;
	getItemKey: (item: T) => string | number;
	minChars?: number;
	debounceMs?: number;
	label?: string;
	helperText?: string;
	disabled?: boolean;
	className?: string;
}

const Autocomplete = <T,>({
	placeholder = 'Search...',
	onSearch,
	onSelect,
	onClear,
	renderItem,
	getItemKey,
	minChars = 2,
	debounceMs = 300,
	label,
	helperText,
	disabled = false,
	className,
}: AutocompleteProps<T>) => {
	const [query, setQuery] = useState('');
	const [results, setResults] = useState<T[]>([]);
	const [isSearching, setIsSearching] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const [hasSearched, setHasSearched] = useState(false);

	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const searchCounterRef = useRef(0);

	const performSearch = useCallback(
		async (searchQuery: string) => {
			if (searchQuery.length < minChars) {
				setResults([]);
				setIsOpen(false);
				setHasSearched(false);
				return;
			}

			const currentCount = ++searchCounterRef.current;
			setIsSearching(true);
			setHasSearched(true);
			setIsOpen(true);

			try {
				const data = await onSearch(searchQuery);
				if (searchCounterRef.current === currentCount) {
					setResults(data);
				}
			} catch {
				if (searchCounterRef.current === currentCount) {
					setResults([]);
				}
			} finally {
				if (searchCounterRef.current === currentCount) {
					setIsSearching(false);
				}
			}
		},
		[onSearch, minChars]
	);

	const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setQuery(value);

		if (debounceTimerRef.current) {
			clearTimeout(debounceTimerRef.current);
		}

		debounceTimerRef.current = setTimeout(() => {
			performSearch(value.trim());
		}, debounceMs);
	};

	const handleSelect = (item: T) => {
		onSelect(item);
		setIsOpen(false);
		setQuery('');
		setResults([]);
		setHasSearched(false);
	};

	const handleClear = () => {
		setQuery('');
		setResults([]);
		setIsOpen(false);
		setHasSearched(false);
		searchCounterRef.current++;
		onClear?.();
		inputRef.current?.focus();
	};

	// Cleanup debounce timer on unmount
	useEffect(() => {
		return () => {
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
			}
		};
	}, []);

	// Outside click and Escape key
	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setIsOpen(false);
			}
		};

		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				setIsOpen(false);
				inputRef.current?.blur();
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		document.addEventListener('keydown', handleEscape);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('keydown', handleEscape);
		};
	}, []);

	return (
		<div className={`autocomplete ${className ?? ''}`} ref={containerRef}>
			{label && <label className="provider-form__label">{label}</label>}
			<div className="autocomplete__input-wrapper">
				<MagnifyingGlassIcon className="autocomplete__search-icon" />
				<input
					ref={inputRef}
					type="text"
					className="autocomplete__input"
					placeholder={placeholder}
					value={query}
					onChange={handleInputChange}
					disabled={disabled}
					onFocus={() => {
						if (results.length > 0 && query.length >= minChars) {
							setIsOpen(true);
						}
					}}
				/>
				{isSearching && <Spinner size="sm" className="autocomplete__spinner" />}
				{query && !isSearching && (
					<button
						type="button"
						className="autocomplete__clear"
						onClick={handleClear}
						aria-label="Clear search"
					>
						<XMarkIcon className="w-4 h-4" />
					</button>
				)}
			</div>
			{helperText && <span className="autocomplete__helper">{helperText}</span>}

			{isOpen && (
				<div className="autocomplete__dropdown">
					{isSearching ? (
						<div className="autocomplete__loading">
							<Spinner size="sm" />
							<span>Searching...</span>
						</div>
					) : results.length > 0 ? (
						<ul className="autocomplete__list">
							{results.map(item => (
								<li
									key={getItemKey(item)}
									className="autocomplete__item"
									onClick={() => handleSelect(item)}
								>
									{renderItem(item)}
								</li>
							))}
						</ul>
					) : hasSearched ? (
						<div className="autocomplete__empty">No results found</div>
					) : null}
				</div>
			)}
		</div>
	);
};

export default Autocomplete;
